/**
 * Student type definitions matching backend schemas exactly.
 * These types ensure type safety and consistency with the API.
 */
import { z } from "zod"

// Base student properties matching StudentBase schema
export interface StudentBase {
  first_name: string
  last_name: string
  date_of_birth: string
  gender: "male" | "female" | "other" | "prefer_not_to_say" | null
  household_id: string
  current_swim_level: string | null
  skill_level_notes: string | null
  has_asthma: boolean | null
  preferred_location: string | null
  notes: string | null
  is_active: boolean | null
  photo_url: string | null
}

// Create student data matching StudentCreate schema
export interface StudentCreate extends StudentBase {}

// Update student data matching StudentUpdate schema
export interface StudentUpdate {
  first_name?: string
  last_name?: string
  date_of_birth?: string
  gender?: "male" | "female" | "other" | "prefer_not_to_say" | null
  household_id?: string
  current_swim_level?: string | null
  skill_level_notes?: string | null
  has_asthma?: boolean | null
  preferred_location?: string | null
  notes?: string | null
  is_active?: boolean | null
  photo_url?: string | null
}

// Full student response matching StudentResponse schema
export interface StudentPublic extends StudentBase {
  id: string
  created_by: string
  created_at: string
  updated_by: string
  updated_at: string
}

// Response wrapper for API calls
export interface StudentResponse extends StudentPublic {
  // Enriched field populated by the backend
  household_name?: string | null
}

// Paginated list response matching StudentListResponse schema
export interface StudentListResponse {
  items: StudentResponse[]
  total: number
  page: number
  per_page: number
  pages: number
  meta: StudentListMeta
}

export interface StudentListMeta {
  sort_by: "created_at" | "updated_at"
  sort_order: "asc" | "desc"
  filters: StudentFilters
}

// Form data interfaces for UI components
export interface StudentFormData {
  first_name?: string
  last_name?: string
  date_of_birth?: string
  gender?: "male" | "female" | "other" | "prefer_not_to_say" | null
  household_id?: string
  current_swim_level?: string | null
  skill_level_notes?: string | null
  has_asthma?: boolean | null
  preferred_location?: string | null
  notes?: string | null
  is_active?: boolean | null
  photo_url?: string | null
}

export interface StudentUpdateFormData {
  first_name?: string
  last_name?: string
  date_of_birth?: string
  gender?: "male" | "female" | "other" | "prefer_not_to_say" | null
  household_id?: string
  current_swim_level?: string | null
  skill_level_notes?: string | null
  has_asthma?: boolean | null
  preferred_location?: string | null
  notes?: string | null
  is_active?: boolean | null
  photo_url?: string | null
}

// Filter interface for API queries
export interface StudentFilters {
  limit?: number
  skip?: number
  search?: string
  sort_by?: "created_at" | "updated_at"
  sort_order?: "asc" | "desc"
  created_by?: string
  created_after?: string
  created_before?: string
  updated_after?: string
  updated_before?: string
  /** Disable the query (useful when dependencies aren't ready) */
  enabled?: boolean
}

// Component prop interfaces following sophisticated pattern
export interface AddStudentProps {
  onSuccess?: () => void
  onCancel?: () => void
}

export interface EditStudentProps {
  student: StudentResponse
  onSuccess?: () => void
  onCancel?: () => void
}

export interface DeleteStudentProps {
  student: StudentResponse
  onSuccess?: () => void
  onCancel?: () => void
}

// Zod validation schemas for forms
export const studentCreateSchema = z.object({
  first_name: z.string().trim(),
  last_name: z.string().trim(),
  date_of_birth: z.string(),
  gender: z
    .enum(["male", "female", "other", "prefer_not_to_say"])
    .nullable()
    .optional(),
  household_id: z.string().trim().max(32),
  current_swim_level: z.string().trim().nullable().optional(),
  skill_level_notes: z.string().trim().nullable().optional(),
  has_asthma: z.boolean().nullable().optional(),
  preferred_location: z.string().trim().nullable().optional(),
  notes: z.string().trim().nullable().optional(),
  is_active: z.boolean().nullable().optional(),
  photo_url: z.string().trim().nullable().optional(),
})

export const studentUpdateSchema = z.object({
  first_name: z.string().trim().optional(),
  last_name: z.string().trim().optional(),
  date_of_birth: z.string().optional(),
  gender: z
    .enum(["male", "female", "other", "prefer_not_to_say"])
    .nullable()
    .optional(),
  household_id: z.string().trim().max(32).optional(),
  current_swim_level: z.string().trim().nullable().optional(),
  skill_level_notes: z.string().trim().nullable().optional(),
  has_asthma: z.boolean().nullable().optional(),
  preferred_location: z.string().trim().nullable().optional(),
  notes: z.string().trim().nullable().optional(),
  is_active: z.boolean().nullable().optional(),
  photo_url: z.string().trim().nullable().optional(),
})

// Type inference from Zod schemas
export type StudentCreateInput = z.infer<typeof studentCreateSchema>
export type StudentUpdateInput = z.infer<typeof studentUpdateSchema>

// Wizard-specific schema (subset of fields for onboarding)
export const studentWizardSchema = z.object({
  first_name: z.string().min(1, "First name is required"),
  last_name: z.string().min(1, "Last name is required"),
  date_of_birth: z.string().min(1, "Date of birth is required"),
  current_swim_level: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
})

export type StudentWizardData = z.infer<typeof studentWizardSchema>
